import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, updateTrip, publishTrip, unpublishTrip, deleteTrip } from '../../../services/firestore/trips';
import { useAuth } from '../../../contexts/AuthContext';
import { uploadImage } from '../../../services/storage';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { ArrowRight, ArrowLeft, X, Check, CheckCircle, ExternalLink, Trash2, Eye, EyeOff } from 'lucide-react';
import { STEPS } from '../components/listing/constants';
import { useListingForm } from '../components/listing/useListingForm';
import StepBasicInfo from '../components/listing/StepBasicInfo';
import StepDescription from '../components/listing/StepDescription';
import StepPhotos from '../components/listing/StepPhotos';
import StepAmenities from '../components/listing/StepAmenities';
import StepPricing from '../components/listing/StepPricing';
import StepAvailability from '../components/listing/StepAvailability';
import StepPackages from '../components/listing/StepPackages';
import StepInstructorInfo from '../components/listing/StepInstructorInfo';
import StepReviews from '../components/listing/StepReviews';
import StepReview from '../components/listing/StepReview';

const EditListing = () => {
  const { tripId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const { formData, setFormData, handleInputChange, toggleAmenity, validateStep } = useListingForm();
  
  // Override setFormData to handle full object replacement
  const updateFormData = (data) => {
    if (typeof data === 'function') {
      setFormData(data);
    } else {
      setFormData((prev) => ({ ...prev, ...data }));
    }
  };
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [currentStatus, setCurrentStatus] = useState('draft');
  const [publishStatus, setPublishStatus] = useState('draft');
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const hasLoadedTrip = useRef(false);
  const loadedTripId = useRef(null);

  useEffect(() => {
    const loadTrip = async () => {
      // Only load if tripId exists, hasn't been loaded yet, or tripId changed
      if (!tripId || (hasLoadedTrip.current && loadedTripId.current === tripId)) {
        console.log('[EditListing] Skipping load - already loaded or no tripId', { tripId, hasLoaded: hasLoadedTrip.current, loadedId: loadedTripId.current });
        return;
      }
      
      console.log('[EditListing] Starting to load trip', { tripId });
      hasLoadedTrip.current = true;
      loadedTripId.current = tripId;
      
      try {
        const trip = await getTrip(tripId);
        if (trip) {
          const formatDate = (dateValue) => {
            if (!dateValue) return '';
            if (dateValue.toDate) {
              return dateValue.toDate().toISOString().split('T')[0];
            }
            if (typeof dateValue === 'string') {
              return dateValue.split('T')[0];
            }
            return '';
          };

          const loadedData = {
            title: trip.title || '',
            location: trip.location || '',
            startDate: formatDate(trip.startDate),
            endDate: formatDate(trip.endDate),
            capacity: trip.capacity || 1,
            basePrice: trip.basePrice || trip.price || 0,
            price: trip.price || 0,
            dailyPrice: trip.dailyPrice || 0,
            pricingModel: trip.pricingModel || 'base', // Load pricingModel, default to 'base' for backward compatibility
            currency: trip.currency || 'USD',
            description: trip.description || '',
            highlights: trip.highlights || '',
            whatIncluded: trip.whatIncluded || '',
            whatToBring: trip.whatToBring || '',
            photos: trip.photos || [],
            amenities: trip.amenities || [],
            bookingWindow: trip.bookingWindow || 30,
            minStay: trip.minStay || 1,
            maxStay: trip.maxStay || '',
            instantBooking: trip.instantBooking || false,
            blockedDates: trip.blockedDates || [],
            reviews: trip.reviews || [],
            packages: trip.packages || [],
            instructorName: trip.instructorName || '',
            instructorBio: trip.instructorBio || '',
            instructorEmail: trip.instructorEmail || '',
            instructorWebsite: trip.instructorWebsite || '',
            instructorInstagram: trip.instructorInstagram || '',
          };

          console.log('[EditListing] Loaded trip data - updating formData', { 
            tripId, 
            blockedDates: loadedData.blockedDates,
            blockedDatesLength: loadedData.blockedDates.length 
          });
          
          // Update formData with loaded data (only happens once on mount)
          updateFormData(loadedData);
          setExistingPhotos(trip.photos || []);
          
          const status = trip.status || 'draft';
          setCurrentStatus(status);
          setPublishStatus(status);
        }
      } catch (error) {
        console.error('[EditListing] Error loading trip:', error);
        setError('Failed to load listing data');
        hasLoadedTrip.current = false; // Allow retry on error
        loadedTripId.current = null;
      } finally {
        setLoading(false);
      }
    };
    loadTrip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]); // Only depend on tripId - setFormData is stable and updateFormData uses it

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalPhotos = existingPhotos.length + photoFiles.length + files.length;
    if (totalPhotos > 10) {
      setError('Maximum 10 photos allowed');
      return;
    }
    
    const newFiles = [...photoFiles, ...files];
    setPhotoFiles(newFiles);
    
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreviews((prev) => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    const newFiles = photoFiles.filter((_, i) => i !== index);
    const newPreviews = photoPreviews.filter((_, i) => i !== index);
    setPhotoFiles(newFiles);
    setPhotoPreviews(newPreviews);
  };

  const removeExistingPhoto = (index) => {
    const newPhotos = existingPhotos.filter((_, i) => i !== index);
    setExistingPhotos(newPhotos);
    updateFormData({ photos: newPhotos });
  };

  const handleNext = () => {
    const validation = validateStep(currentStep);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }
    
    if (currentStep === 3 && existingPhotos.length === 0 && photoFiles.length === 0) {
      setError('Please add at least one photo');
      return;
    }
    
    setError('');
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setError('');
  };

  const handlePublish = async () => {
    if (!tripId) return;
    setError('');
    setSaving(true);
    try {
      await publishTrip(tripId);
      setCurrentStatus('published');
      setPublishStatus('published');
      setSaving(false);
    } catch (err) {
      setError(err.message || 'Failed to publish listing');
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    if (!tripId) return;
    setError('');
    setSaving(true);
    try {
      await unpublishTrip(tripId);
      setCurrentStatus('draft');
      setPublishStatus('draft');
      setSaving(false);
    } catch (err) {
      setError(err.message || 'Failed to unpublish listing');
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tripId) return;
    setDeleting(true);
    try {
      await deleteTrip(tripId);
      navigate('/host/trips');
    } catch (err) {
      setError(err.message || 'Failed to delete listing');
      setDeleting(false);
      setDeleteModal(false);
    }
  };

  const handleDeleteClose = () => {
    if (!deleting) {
      setDeleteModal(false);
    }
  };

  // Save current step to Firebase
  const handleSaveStep = async () => {
    if (!tripId || !user) return;

    // Validate current step before saving
    const validation = validateStep(currentStep);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    // Special validation for photos step
    if (currentStep === 3 && existingPhotos.length === 0 && photoFiles.length === 0) {
      setError('Please add at least one photo');
      return;
    }

    setError('');
    setSaving(true);
    
    // Only set uploadingPhotos if we're actually uploading photos
    if (currentStep === 3 && photoFiles.length > 0) {
      setUploadingPhotos(true);
    }

    try {
      let photosToSave = existingPhotos;
      
      // Upload new photos if we're on the photos step
      if (currentStep === 3 && photoFiles.length > 0) {
        const uploadedPhotos = [];
        for (const file of photoFiles) {
          const path = `trips/${user.uid}/${Date.now()}_${file.name}`;
          const url = await uploadImage(file, path);
          uploadedPhotos.push(url);
        }
        photosToSave = [...existingPhotos, ...uploadedPhotos];
        setExistingPhotos(photosToSave);
        setPhotoFiles([]);
        setPhotoPreviews([]);
      }

      // Prepare update data with all current formData (including reviews, packages, etc.)
      const updateData = {
        ...formData,
        price: formData.basePrice || formData.price || 0,
      };

      // Include photos if we're on the photos step
      if (currentStep === 3) {
        updateData.photos = photosToSave;
      }

      // Save to Firebase - this will persist all formData including reviews
      await updateTrip(tripId, updateData);

      setSaving(false);
      setUploadingPhotos(false);
      
      // Show success message
      setError('Changes saved successfully!');
      setTimeout(() => {
        setError('');
      }, 3000);
    } catch (err) {
      console.error('[EditListing] Error saving step:', err);
      setError(err.message || 'Failed to save changes');
      setSaving(false);
      setUploadingPhotos(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tripId || !user) return;

    setError('');
    setSaving(true);
    setUploadingPhotos(true);

    try {
      const uploadedPhotos = [];
      for (const file of photoFiles) {
        const path = `trips/${user.uid}/${Date.now()}_${file.name}`;
        const url = await uploadImage(file, path);
        uploadedPhotos.push(url);
      }

      const allPhotos = [...existingPhotos, ...uploadedPhotos];

      await updateTrip(tripId, {
        ...formData,
        price: formData.basePrice || formData.price || 0,
        photos: allPhotos,
        status: publishStatus,
      });

      navigate('/host/trips');
    } catch (err) {
      setError(err.message || 'Failed to update listing');
      setSaving(false);
      setUploadingPhotos(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <StepBasicInfo formData={formData} handleInputChange={handleInputChange} />;
      case 2:
        return <StepDescription formData={formData} handleInputChange={handleInputChange} />;
      case 3:
        return (
          <StepPhotos
            photoFiles={photoFiles}
            photoPreviews={photoPreviews}
            existingPhotos={existingPhotos}
            handlePhotoSelect={handlePhotoSelect}
            removePhoto={removePhoto}
            removeExistingPhoto={removeExistingPhoto}
          />
        );
      case 4:
        return <StepAmenities formData={formData} toggleAmenity={toggleAmenity} />;
      case 5:
        return <StepPricing formData={formData} handleInputChange={handleInputChange} />;
      case 6:
        return <StepAvailability formData={formData} handleInputChange={handleInputChange} />;
      case 7:
        return <StepPackages formData={formData} handleInputChange={handleInputChange} />;
      case 8:
        return <StepInstructorInfo formData={formData} handleInputChange={handleInputChange} />;
      case 9:
        return <StepReviews formData={formData} handleInputChange={handleInputChange} />;
      case 10:
        return (
          <StepReview
            formData={formData}
            photoPreviews={photoPreviews}
            existingPhotos={existingPhotos}
            publishStatus={publishStatus}
            setPublishStatus={setPublishStatus}
            isApprovedHost={true}
            tripId={tripId}
            onPublish={handlePublish}
            onUnpublish={handleUnpublish}
            currentStatus={currentStatus}
            saving={saving}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  const completedSteps = currentStep - 1;
  const progress = (completedSteps / STEPS.length) * 100;

  return (
    <>
      <DeleteConfirmationModal
        isOpen={deleteModal}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Listing"
        message="Are you sure you want to delete this listing? This will permanently remove it and all associated data."
        itemName={formData.title}
        isDeleting={deleting}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="flex flex-col lg:flex-row h-screen lg:h-auto lg:min-h-screen">
          {/* Sidebar */}
          <div className="hidden lg:flex w-80 bg-white border-r border-gray-200 flex-col flex-shrink-0">
            <div className="p-6 border-b border-gray-200">
              <button
                onClick={() => navigate('/host/trips')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
              >
                <X className="w-5 h-5" />
                <span className="text-sm font-medium">Exit</span>
              </button>
              <h2 className="text-2xl font-light text-gray-900 mb-2">Edit Listing</h2>
              <p className="text-sm text-gray-600">Step {currentStep} of {STEPS.length}</p>
              <div className="mt-3">
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                    currentStatus === 'published'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {currentStatus === 'published' ? (
                    <>
                      <Eye className="w-3 h-3" />
                      Published
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-3 h-3" />
                      Draft
                    </>
                  )}
                </span>
              </div>
              <div className="mt-4">
                <a
                  href={`/trips/${tripId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
                >
                  <ExternalLink className="w-4 h-4" />
                  View as Client
                </a>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {STEPS.map((step, index) => {
                  const StepIcon = step.icon;
                  const stepNumber = index + 1;
                  const isCompleted = stepNumber < currentStep;
                  const isCurrent = stepNumber === currentStep;

                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => {
                        setCurrentStep(stepNumber);
                        setError('');
                      }}
                      className={`w-full flex items-start gap-4 p-4 rounded-lg transition-all text-left ${
                        isCurrent
                          ? 'bg-emerald-50 border-2 border-emerald-500'
                          : isCompleted
                          ? 'bg-emerald-50/30 border-2 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300'
                          : 'border-2 border-transparent hover:bg-gray-50 hover:border-gray-200'
                      }`}
                    >
                      <div
                        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                          isCompleted
                            ? 'bg-emerald-600 text-white'
                            : isCurrent
                            ? 'bg-emerald-600 text-white'
                            : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        {isCompleted ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <StepIcon className="w-5 h-5" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3
                          className={`text-sm font-medium ${
                            isCurrent ? 'text-emerald-900' : isCompleted ? 'text-gray-900' : 'text-gray-500'
                          }`}
                        >
                          {step.title}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">{step.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 text-center">
                {Math.round(progress)}% Complete
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto py-4 sm:py-6 lg:py-8 px-4 sm:px-6 lg:px-8">
              {/* Mobile Header */}
              <div className="lg:hidden mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => navigate('/host/trips')}
                    className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">Step {currentStep} of {STEPS.length}</span>
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                        currentStatus === 'published'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {currentStatus === 'published' ? (
                        <>
                          <Eye className="w-3 h-3" />
                          Published
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-3 h-3" />
                          Draft
                        </>
                      )}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                  <div
                    className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>

              <div className="mb-6 lg:mb-8">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-2">
                  {STEPS[currentStep - 1].title}
                </h1>
                <p className="text-base sm:text-lg text-gray-600 font-light">
                  {STEPS[currentStep - 1].description}
                </p>
              </div>

              <form onSubmit={currentStep === STEPS.length ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl border border-gray-200/50 p-4 sm:p-6 lg:p-8 lg:sm:p-10">
                  {error && (
                    <div className={`rounded-lg border-2 p-4 mb-6 ${
                      error.includes('saved') || error.includes('Saved')
                        ? 'bg-emerald-50 border-emerald-300'
                        : 'bg-red-50 border-red-300'
                    }`}>
                      <p className={`text-sm font-medium ${
                        error.includes('saved') || error.includes('Saved')
                          ? 'text-emerald-800'
                          : 'text-red-800'
                      }`}>
                        {error}
                      </p>
                    </div>
                  )}

                  {renderStepContent()}

                  {/* Navigation Buttons */}
                  <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-4 sm:pt-6 mt-6 sm:mt-8 border-t border-gray-200">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <button
                        type="button"
                        onClick={handleBack}
                        disabled={currentStep === 1 || saving}
                        className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 rounded-lg text-sm sm:text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-initial"
                      >
                        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="hidden sm:inline">Back</span>
                      </button>
                      {currentStep === STEPS.length && (
                        <>
                          <a
                            href={`/trips/${tripId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 border border-blue-300 rounded-lg text-sm sm:text-base font-medium text-blue-700 bg-white hover:bg-blue-50 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span className="hidden sm:inline">View</span>
                          </a>
                          <button
                            type="button"
                            onClick={handleDeleteClick}
                            disabled={saving || deleting}
                            className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 border border-red-300 rounded-lg text-sm sm:text-base font-medium text-red-700 bg-white hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span className="hidden sm:inline">Delete</span>
                          </button>
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3">
                      {/* Save button for all steps */}
                      <button
                        type="button"
                        onClick={handleSaveStep}
                        disabled={saving || uploadingPhotos}
                        className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-medium shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving || uploadingPhotos ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span>Save</span>
                          </>
                        )}
                      </button>

                      {currentStep < STEPS.length ? (
                        <button
                          type="submit"
                          disabled={saving || uploadingPhotos}
                          className="inline-flex items-center justify-center gap-2 group bg-emerald-700 hover:bg-emerald-800 text-white px-4 sm:px-6 py-2.5 sm:py-4 rounded-lg text-sm sm:text-base font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        >
                          Next
                          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                      ) : (
                        <button
                          type="submit"
                          disabled={saving || uploadingPhotos}
                          className="inline-flex items-center justify-center gap-2 group bg-emerald-700 hover:bg-emerald-800 text-white px-4 sm:px-6 py-2.5 sm:py-4 rounded-lg text-sm sm:text-base font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        >
                          {saving || uploadingPhotos ? (
                            'Saving...'
                          ) : (
                            <>
                              Save & Exit
                              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditListing;
